import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  FiSearch, FiDollarSign, FiCalendar, FiRefreshCw, 
  FiFilter, FiCheckCircle, FiXCircle, FiTrendingUp,
  FiClock, FiCreditCard, FiAlertCircle
} from 'react-icons/fi';
import { api } from '../../services/api';

// Formater les montants
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('fr-TN', {
    style: 'currency',
    currency: 'TND',
    minimumFractionDigits: 0
  }).format(amount || 0);
};

// Les montants arrivent en texte depuis PostgreSQL (« 40.00 ») : les convertir avant d'additionner
const paymentAmount = (payment) => {
  const amount = parseFloat(payment?.amount);
  if (Number.isFinite(amount)) return amount;
  const cents = parseFloat(payment?.amount_cents);
  return Number.isFinite(cents) ? cents / 100 : 0;
};

// Formater les dates
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('fr-FR', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

function AdminPayments() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    loadPayments();
  }, []);

  const loadPayments = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.getAllPayments();
      
      console.log('Réponse API getAllPayments:', response); // Debug
      
      if (response && response.success) {
        const paymentsData = response.data || [];
        console.log('Paiements reçus:', paymentsData.length, paymentsData); // Debug
        setPayments(paymentsData);
      } else {
        throw new Error(response?.message || 'Erreur lors du chargement des paiements');
      }
    } catch (err) {
      console.error('Erreur lors du chargement des paiements:', err);
      setError(err.message || 'Erreur lors du chargement des paiements');
      setPayments([]); // S'assurer que payments est un tableau vide en cas d'erreur
    } finally {
      setLoading(false);
    }
  };

  const filteredPayments = payments.filter(payment => {
    const matchesSearch = 
      payment.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      payment.client_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      payment.provider_ref?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || payment.status === filterStatus;
    
    return matchesSearch && matchesStatus;
  });

  const paginatedPayments = filteredPayments.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const totalPages = Math.ceil(filteredPayments.length / itemsPerPage);

  const totalRevenue = payments
    .filter(p => ['succeeded', 'completed', 'paid'].includes(p.status))
    .reduce((sum, p) => sum + paymentAmount(p), 0);

  const pendingAmount = payments
    .filter(p => p.status === 'pending' || p.status === 'processing')
    .reduce((sum, p) => sum + paymentAmount(p), 0);
    
  const failedAmount = payments
    .filter(p => p.status === 'failed' || p.status === 'cancelled')
    .reduce((sum, p) => sum + paymentAmount(p), 0);

  const getStatusColor = (status) => {
    switch(status) {
      case 'succeeded':
      case 'completed':
        return 'bg-green-600/20 text-green-400 border border-green-600/50';
      case 'pending':
      case 'processing':
        return 'bg-yellow-600/20 text-yellow-400 border border-yellow-600/50';
      case 'failed':
      case 'cancelled':
        return 'bg-red-600/20 text-red-400 border border-red-600/50';
      default:
        return 'bg-gray-700 text-gray-300 border border-gray-600';
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      'succeeded': 'Réussi',
      'completed': 'Terminé',
      'pending': 'En attente',
      'processing': 'En traitement',
      'failed': 'Échoué',
      'cancelled': 'Annulé'
    };
    return labels[status] || status;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-yellow-600 mx-auto mb-4"></div>
          <p className="text-gray-400">Chargement des paiements...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-4xl font-bold text-yellow-400 mb-2">
          Gestion des paiements
        </h1>
        <p className="text-gray-400 font-medium">Suivez tous les paiements de la plateforme</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-gray-800 border border-yellow-600/30 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm font-medium text-gray-400 mb-1">Revenus totaux</p>
              <p className="text-4xl font-bold text-yellow-400">{formatCurrency(totalRevenue)}</p>
            </div>
            <div className="p-4 bg-yellow-600/20 border border-yellow-600/50 rounded-lg">
              <FiDollarSign className="w-8 h-8 text-yellow-400" />
            </div>
          </div>
          <div className="flex items-center text-sm text-gray-400">
            <FiTrendingUp className="mr-1" />
            <span>+12% ce mois</span>
          </div>
        </div>
        <div className="bg-gray-800 border border-yellow-600/30 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm font-medium text-gray-400 mb-1">En attente</p>
              <p className="text-4xl font-bold text-yellow-400">{formatCurrency(pendingAmount)}</p>
            </div>
            <div className="p-4 bg-yellow-600/20 border border-yellow-600/50 rounded-lg">
              <FiClock className="w-8 h-8 text-yellow-400" />
            </div>
          </div>
          <div className="flex items-center text-sm text-gray-400">
            <span>{payments.filter(p => p.status === 'pending').length} transactions</span>
          </div>
        </div>
        <div className="bg-gray-800 border border-yellow-600/30 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm font-medium text-gray-400 mb-1">Total transactions</p>
              <p className="text-4xl font-bold text-yellow-400">{payments.length}</p>
            </div>
            <div className="p-4 bg-yellow-600/20 border border-yellow-600/50 rounded-lg">
              <FiCreditCard className="w-8 h-8 text-yellow-400" />
            </div>
          </div>
          <div className="flex items-center text-sm text-gray-400">
            <FiTrendingUp className="mr-1" />
            <span>Toutes périodes</span>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-gray-800 border border-yellow-600/30 rounded-lg p-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <FiSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher par client..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-gray-900 border border-gray-700 text-white rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 placeholder-gray-500 transition-all"
            />
          </div>
          <div className="flex items-center space-x-3">
            <FiFilter className="text-gray-400" />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-3 bg-gray-900 border border-gray-700 text-white rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
            >
              <option value="all">Tous les statuts</option>
              <option value="succeeded">Réussi</option>
              <option value="completed">Terminé</option>
              <option value="pending">En attente</option>
              <option value="processing">En traitement</option>
              <option value="failed">Échoué</option>
              <option value="cancelled">Annulé</option>
            </select>
          </div>
          <button
            onClick={loadPayments}
            className="px-5 py-3 bg-yellow-600 text-gray-900 rounded-lg hover:bg-yellow-500 transition-all duration-300 flex items-center font-semibold"
          >
            <FiRefreshCw className="mr-2" />
            Actualiser
          </button>
        </div>
      </div>

      {/* Payments Table */}
      <div className="bg-gray-800 border border-yellow-600/30 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-700">
            <thead className="bg-gray-900">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-300 uppercase tracking-wider">
                  Client
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-300 uppercase tracking-wider">
                  Montant
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-300 uppercase tracking-wider">
                  Méthode
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-300 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-300 uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-gray-800 divide-y divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center">
                    <div className="flex justify-center">
                      <div className="relative">
                        <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-700"></div>
                        <div className="animate-spin rounded-full h-12 w-12 border-4 border-transparent border-t-yellow-500 absolute top-0 left-0"></div>
                      </div>
                    </div>
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center">
                      <FiAlertCircle className="text-red-500 text-4xl mb-2" />
                      <p className="text-red-400 font-semibold">{error}</p>
                      <button
                        onClick={loadPayments}
                        className="mt-4 px-4 py-2 bg-yellow-600 text-gray-900 rounded-lg hover:bg-yellow-500 transition-all"
                      >
                        Réessayer
                      </button>
                    </div>
                  </td>
                </tr>
              ) : paginatedPayments.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-gray-400">
                    <div className="flex flex-col items-center">
                      <FiAlertCircle className="text-gray-500 text-4xl mb-2" />
                      <p>Aucun paiement trouvé</p>
                      {payments.length === 0 && (
                        <p className="text-sm text-gray-500 mt-2">Aucune donnée dans la base de données</p>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                paginatedPayments.map((payment) => (
                  <tr key={payment.id} className="hover:bg-gray-700/50 transition-all duration-200">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-bold text-white">
                        {payment.client_name || 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-lg font-bold text-yellow-400">
                        {formatCurrency(payment.amount)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-300 flex items-center">
                        <FiCreditCard className="mr-2 text-gray-500" />
                        {payment.provider || 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-400 flex items-center">
                        <FiCalendar className="mr-2 text-gray-500" />
                        {formatDate(payment.paid_at || payment.created_at)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-3 py-1 inline-flex text-xs font-bold rounded-full ${getStatusColor(payment.status)}`}>
                        {getStatusLabel(payment.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold">
                      <Link
                        to={`/admin/payments/${payment.id}`}
                        className="text-yellow-400 hover:text-yellow-300 hover:underline transition-colors"
                      >
                        Voir détails
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-gray-900 px-6 py-4 flex items-center justify-between border-t border-gray-700">
            <div className="text-sm text-gray-400 font-medium">
              Affichage de {(currentPage - 1) * itemsPerPage + 1} à {Math.min(currentPage * itemsPerPage, filteredPayments.length)} sur {filteredPayments.length} paiements
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 border border-gray-700 text-gray-300 rounded-lg text-sm font-semibold bg-gray-800 hover:bg-gray-700 hover:border-yellow-600/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                Précédent
              </button>
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="px-4 py-2 border border-gray-700 text-gray-300 rounded-lg text-sm font-semibold bg-gray-800 hover:bg-gray-700 hover:border-yellow-600/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                Suivant
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminPayments;
